import { useState, useEffect } from 'react'
import { PlusIcon, XMarkIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { norm } from '../../utils/normalize'
import { fmtDate } from '../../utils/format'
import SortableHeader, { useSort } from '../../components/SortableHeader'

interface Allergen {
  id: number
  name: string
  code: string
  svg_icon?: string
  created_at: string
  updated_at: string
}

const API_BASE_URL = '/api/v1/dev'

export default function Allergens() {
  const [allergens, setAllergens] = useState<Allergen[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingAllergen, setEditingAllergen] = useState<Allergen | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    svg_icon: ''
  })
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Debug: Verify this is the correct component
  console.log('🔥 ALLERGENS COMPONENT LOADED - CONNECTED TO API', new Date().toISOString())

  // Fetch allergens from API
  const fetchAllergens = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔄 FETCHING ALLERGENS FROM API:', `${API_BASE_URL}/allergens`)
      const response = await fetch(`${API_BASE_URL}/allergens`)
      const data = await response.json()
      console.log('📥 API RESPONSE:', data)

      if (response.ok) {
        setAllergens(data.data)
        console.log('✅ ALLERGENS LOADED:', data.data.length, 'items')
      } else {
        setError('Error al cargar alérgenos')
        console.error('❌ API ERROR:', data)
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('❌ FETCH ERROR:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllergens()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      let response
      if (editingAllergen) {
        // Update allergen
        response = await fetch(`${API_BASE_URL}/allergens/${editingAllergen.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        })
      } else {
        // Create new allergen
        response = await fetch(`${API_BASE_URL}/allergens`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        })
      }

      const data = await response.json()

      if (response.ok) {
        // Refresh the allergens list
        await fetchAllergens()
        closeModal()
      } else {
        if (data.errors) {
          // Display validation errors
          const errorMessages = Object.values(data.errors).flat().join(', ')
          setError(errorMessages)
        } else {
          setError(data.message || 'Error al guardar alérgeno')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving allergen:', err)
    } finally {
      setSaving(false)
    }
  }

  const openModal = (allergen?: Allergen) => {
    if (allergen) {
      setEditingAllergen(allergen)
      setFormData({
        name: allergen.name,
        code: allergen.code,
        svg_icon: allergen.svg_icon || ''
      })
    } else {
      setEditingAllergen(null)
      setFormData({ name: '', code: '', svg_icon: '' })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingAllergen(null)
    setFormData({ name: '', code: '', svg_icon: '' })
    setError(null)
  }

  const deleteAllergen = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este alérgeno?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/allergens/${id}`, {
        method: 'DELETE',
      })

      const data = await response.json()

      if (response.ok) {
        // Refresh the allergens list
        await fetchAllergens()
      } else {
        setError(data.message || 'Error al eliminar alérgeno')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting allergen:', err)
    }
  }

  const [currentPage, setCurrentPage] = useState(1)
  const { sortField, sortDirection, handleSort, sortData } = useSort<Allergen>('name')
  const perPage = 15

  const filteredAllergens = allergens.filter((a) => {
    const q = norm(search)
    return norm(a.name).includes(q) || norm(a.code).includes(q)
  })

  const sortedAllergens = sortData(filteredAllergens)
  const totalPages = Math.ceil(filteredAllergens.length / perPage)
  const paginatedAllergens = sortedAllergens.slice((currentPage - 1) * perPage, currentPage * perPage)

  useEffect(() => { setCurrentPage(1) }, [search])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Alérgenos</h1>
            <p className="mt-1 text-sm text-gray-600">
              Administra los 14 alérgenos oficiales de la UE según normativa
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              type="button"
              onClick={() => openModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Nuevo Alérgeno
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Cerrar</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert for EU Regulation */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              Según el Reglamento (UE) Nº 1169/2011, es obligatorio informar sobre los 14 alérgenos principales.
              Esta lista debe mantenerse actualizada según la normativa vigente.
            </p>
          </div>
        </div>
      </div>

      {/* Allergens Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Alérgenos Registrados ({filteredAllergens.length}{search ? ` de ${allergens.length}` : ''})
          </h3>
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o código..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 w-64"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Icono
                </th>
                <SortableHeader label="Nombre" field="name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="Código" field="code" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Creación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última Actualización
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedAllergens.map((allergen) => (
                <tr key={allergen.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-2xl">{allergen.svg_icon?.startsWith('<svg') ? <span dangerouslySetInnerHTML={{ __html: allergen.svg_icon }} /> : allergen.svg_icon}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{allergen.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-500">{allergen.code}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fmtDate(allergen.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {fmtDate(allergen.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(allergen)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteAllergen(allergen.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages} ({filteredAllergens.length} alérgenos)
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-11/12 max-w-4xl">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingAllergen ? 'Editar Alérgeno' : 'Nuevo Alérgeno'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nombre del Alérgeno
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Código (slug)
                      </label>
                      <input
                        type="text"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="ej: frutos-cascara"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Icono SVG o Emoji
                      </label>
                      <input
                        type="text"
                        value={formData.svg_icon}
                        onChange={(e) => setFormData({ ...formData, svg_icon: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Pegue aquí el código SVG o un emoji"
                      />
                      {formData.svg_icon && (
                        <div className="mt-2 text-center">
                          <span className="text-4xl">{formData.svg_icon?.startsWith('<svg') ? <span dangerouslySetInnerHTML={{ __html: formData.svg_icon }} /> : formData.svg_icon}</span>
                        </div>
                      )}
                    </div>

                    {/* Error display in modal */}
                    {error && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Guardando...
                      </div>
                    ) : (
                      editingAllergen ? 'Actualizar' : 'Crear'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}